import React, { useEffect, useRef, useState } from "react";
import { HMSVirtualBackgroundTypes } from "@100mslive/hms-virtual-background";
import { CloudUploadIcon, CrossIcon } from "@100mslive/react-icons";
import {
  Box,
  Button,
  Dialog,
  Flex,
  IconButton,
  Text,
} from "@100mslive/roomkit-react";
import { DialogContent, DialogCol } from "../../primitives/DialogContent";
import { getRandomVirtualBackground } from "./vbutils";

// Default random background images (from vbutils.js)
const defaultRandomImages = [
  "https://www.100ms.live/images/vb-1.jpeg",
  "https://www.100ms.live/images/vb-2.jpg",
  "https://www.100ms.live/images/vb-3.png",
  "https://d2qi07yyjujoxr.cloudfront.net/webapp/vb/hms1.png",
  "https://d2qi07yyjujoxr.cloudfront.net/webapp/vb/hms2.png",
];

export const VirtualBackgroundModal = ({
  open,
  onOpenChange,
  pluginRef,
  hmsActions,
  role,
  onBackgroundApplied,
  onRemoveBackground,
}) => {
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedEffect, setSelectedEffect] = useState("none");
  const [predefinedBackgrounds, setPredefinedBackgrounds] = useState([]);

  const applyBackground = async (background, backgroundType) => {
    if (!pluginRef.current) {
      return;
    }

    // Prevent multiple simultaneous calls
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    try {
      await pluginRef.current.setBackground(background, backgroundType);
      if (onBackgroundApplied) {
        onBackgroundApplied();
      }
      onOpenChange(false);
    } catch (error) {
      console.error("Error applying background:", error);
      alert("Failed to apply background");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    setIsProcessing(true);
    try {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = async () => {
        try {
          setSelectedEffect("image");
          await applyBackground(img, HMSVirtualBackgroundTypes.IMAGE);
          URL.revokeObjectURL(objectUrl);
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          throw error;
        }
      };

      img.onerror = () => {
        alert("Failed to load image");
        URL.revokeObjectURL(objectUrl);
        setIsProcessing(false);
      };

      img.src = objectUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image");
      setIsProcessing(false);
    }
  };

  const handleBlur = async () => {
    setSelectedEffect("blur");
    await applyBackground(
      HMSVirtualBackgroundTypes.BLUR,
      HMSVirtualBackgroundTypes.BLUR
    );
  };

  const handleRandomBackground = async () => {
    try {
      const { background, backgroundType } = getRandomVirtualBackground();
      await applyBackground(background, backgroundType);
    } catch (error) {
      console.error("Error applying random background:", error);
      alert("Failed to apply random background");
    }
  };

  const handleNoEffect = async () => {
    setSelectedEffect("none");
    if (onRemoveBackground) {
      setIsProcessing(true);
      try {
        await onRemoveBackground();
        if (onBackgroundApplied) {
          onBackgroundApplied();
        }
        onOpenChange(false);
      } catch (error) {
        console.error("Error removing background:", error);
        alert("Failed to remove background");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleBlurEffect = async () => {
    setSelectedEffect("blur");
    await handleBlur();
  };

  const handlePredefinedBackground = async imagePath => {
    if (!imagePath) {
      return;
    }

    try {
      setIsProcessing(true);
      const img = new Image();
      const publicUrl = process.env.PUBLIC_URL || "";
      const resolvedPath =
        imagePath.startsWith("http://") || imagePath.startsWith("https://")
          ? imagePath
          : `${publicUrl}${imagePath}`;

      img.src = resolvedPath;
      img.onload = async () => {
        try {
          setSelectedEffect("image");
          await applyBackground(img, HMSVirtualBackgroundTypes.IMAGE);
        } catch (error) {
          console.error("Error applying predefined background:", error);
          alert("Failed to apply background");
        } finally {
          setIsProcessing(false);
        }
      };
      img.onerror = () => {
        console.error("Failed to load predefined background:", resolvedPath);
        alert("Failed to load image");
        setIsProcessing(false);
      };
    } catch (error) {
      console.error("Error preparing predefined background:", error);
      alert("Failed to apply background");
      setIsProcessing(false);
    }
  };

  // Load predefined backgrounds from the virtual-backgrounds folder
  useEffect(() => {
    const loadBackgrounds = async () => {
      try {
        const publicUrl = process.env.PUBLIC_URL || "";
        const maxBackgrounds = 5;
        let customImages = [];

        // Try loading from a manifest file first, if it exists
        try {
          const manifestResponse = await fetch(
            `${publicUrl}/virtual-backgrounds/manifest.json`
          );
          if (manifestResponse.ok) {
            const manifest = await manifestResponse.json();
            customImages = Array.isArray(manifest)
              ? manifest.filter(img => img && img.trim())
              : [];
          }
        } catch (e) {
          // Manifest doesn't exist or failed to load, continue with defaults
        }

        // Combine custom images with default random images
        // Custom images come first, then fill remaining slots with defaults
        const combinedImages = [...customImages];
        const remainingSlots = maxBackgrounds - combinedImages.length;
        
        if (remainingSlots > 0) {
          combinedImages.push(...defaultRandomImages.slice(0, remainingSlots));
        }

        // Limit to maxBackgrounds total
        setPredefinedBackgrounds(combinedImages.slice(0, maxBackgrounds));
      } catch (error) {
        console.error("Error loading predefined backgrounds:", error);
        // Fall back to default random images on error
        setPredefinedBackgrounds(defaultRandomImages.slice(0, 5));
      }
    };

    if (open) {
      loadBackgrounds();
    }
  }, [open]);

  // Blur icon component (simple SVG representation)
  const BlurIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.8" />
      <circle cx="8" cy="8" r="2" fill="currentColor" opacity="0.6" />
      <circle cx="16" cy="8" r="2" fill="currentColor" opacity="0.6" />
      <circle cx="8" cy="16" r="2" fill="currentColor" opacity="0.6" />
      <circle cx="16" cy="16" r="2" fill="currentColor" opacity="0.6" />
    </svg>
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Virtual Background" css={{ width: "min(500px, 90%)" }}>
        <Flex direction="column" css={{ gap: "$10" }}>
          {/* Effects Section */}
          <Flex direction="column" css={{ gap: "$4" }}>
            <Text variant="body2" css={{ color: "#191B23", fontWeight: "$semiBold" }}>
              Effects
            </Text>
            <Flex css={{ gap: "$4" }}>
              {/* No Effect Button */}
              <Box
                role="button"
                onClick={handleNoEffect}
                disabled={isProcessing}
                css={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "$2",
                  width: "80px",
                  height: "80px",
                  borderRadius: "$round",
                  border:
                    selectedEffect === "none"
                      ? "2px solid $primary_default"
                      : "2px solid $border_bright",
                  backgroundColor:
                    selectedEffect === "none"
                      ? "$surface_bright"
                      : "transparent",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  opacity: isProcessing ? 0.6 : 1,
                  "&:hover": {
                    borderColor:
                      selectedEffect === "none"
                        ? "$primary_default"
                        : "$on_surface_medium",
                    backgroundColor: "$surface_bright",
                  },
                  transition: "all 0.2s",
                }}
              >
                <Box
                  css={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "$on_surface_high",
                  }}
                >
                  <CrossIcon width={24} height={24} />
                </Box>
                <Text
                  variant="caption"
                  css={{
                    color: "$on_surface_high",
                    fontSize: "$xs",
                    textAlign: "center",
                  }}
                >
                  No effect
                </Text>
              </Box>

              {/* Blur Button */}
              <Box
                role="button"
                onClick={handleBlurEffect}
                disabled={isProcessing}
                css={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "$2",
                  width: "80px",
                  height: "80px",
                  borderRadius: "$round",
                  border:
                    selectedEffect === "blur"
                      ? "2px solid $primary_default"
                      : "2px solid $border_bright",
                  backgroundColor:
                    selectedEffect === "blur" ? "$surface_bright" : "transparent",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  opacity: isProcessing ? 0.6 : 1,
                  "&:hover": {
                    borderColor:
                      selectedEffect === "blur"
                        ? "$primary_default"
                        : "$on_surface_medium",
                    backgroundColor: "$surface_bright",
                  },
                  transition: "all 0.2s",
                }}
              >
                <Box
                  css={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "$on_surface_high",
                  }}
                >
                  <BlurIcon />
                </Box>
                <Text
                  variant="caption"
                  css={{
                    color: "$on_surface_high",
                    fontSize: "$xs",
                    textAlign: "center",
                  }}
                >
                  Blur
                </Text>
              </Box>
            </Flex>
          </Flex>

          {/* Backgrounds Section */}
          {predefinedBackgrounds.length > 0 && (
            <Flex direction="column" css={{ gap: "$4" }}>
              <Text variant="body2" css={{ color: "#191B23", fontWeight: "$semiBold" }}>
                Backgrounds
              </Text>
              <Flex
                css={{
                  gap: "$4",
                  flexWrap: "wrap",
                  w: "100%",
                }}
              >
                {predefinedBackgrounds.map((imagePath, index) => {
                  const publicUrl = process.env.PUBLIC_URL || "";
                  const resolvedPath =
                    imagePath.startsWith("http://") ||
                    imagePath.startsWith("https://")
                      ? imagePath
                      : `${publicUrl}${imagePath}`;

                  return (
                    <Box
                      key={resolvedPath || index}
                      role="button"
                      onClick={() =>
                        !isProcessing && handlePredefinedBackground(imagePath)
                      }
                      css={{
                        width: "90px",
                        height: "60px",
                        borderRadius: "$2",
                        overflow: "hidden",
                        border: "1px solid $border_bright",
                        cursor: isProcessing ? "not-allowed" : "pointer",
                        position: "relative",
                        backgroundColor: "$surface_bright",
                        "&:hover": {
                          borderColor: isProcessing
                            ? "$border_bright"
                            : "$primary_default",
                          transform: "scale(1.02)",
                        },
                        transition: "all 0.2s",
                        opacity: isProcessing ? 0.6 : 1,
                      }}
                    >
                      <img
                        src={resolvedPath}
                        alt={`Background ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                        onError={e => {
                          e.target.style.display = "none";
                        }}
                      />
                    </Box>
                  );
                })}
              </Flex>
            </Flex>
          )}

          {/* Upload Section */}
          <Flex direction="column" css={{ gap: "$4" }}>
            <Text variant="body2" css={{ color: "#191B23", fontWeight: "$semiBold" }}>
              Upload your own image
            </Text>
            <DialogCol
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              css={{
                justifyContent: "center",
                position: "relative",
                cursor: isProcessing ? "not-allowed" : "pointer",
                py: "$12",
                border: "1px dashed $border_bright",
                r: "$1",
                height: "max(140px, 30%)",
                alignItems: "center",
                m: "$6 0",
                opacity: isProcessing ? 0.6 : 1,
                "&:hover": {
                  borderColor: isProcessing ? "$border_bright" : "$primary_default",
                },
              }}
              gap="8"
            >
              <IconButton
                variant="standard"
                disabled={isProcessing}
                css={{
                  border: "none",
                  background: "none",
                  "&:hover": {
                    border: "none",
                    background: "none",
                    bg: "$transparent !important",
                  },
                }}
              >
                <CloudUploadIcon
                  style={{
                    width: "3rem",
                    height: "3rem",
                    color: "#2572ED",
                  }}
                />
              </IconButton>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: "none" }}
                disabled={isProcessing}
              />
              <Text
                variant="md"
                css={{
                  fontWeight: "$semiBold",
                  color: "#191B23",
                  textDecoration: "underline",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                }}
              >
                Click to upload image
              </Text>
            </DialogCol>
          </Flex>
        </Flex>
      </DialogContent>
    </Dialog.Root>
  );
};

